import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Globe, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { LocationMap } from "@/components/LocationMap";
import { fallbackContact, telHref, mailHref } from "@/lib/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const { t } = useLanguage();
  const { content } = useContent();
  const contact = content?.contact ?? fallbackContact;
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | sending | success

  const f = t.contact.form;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(initial);
      toast.success(f.success);
    } catch (err) {
      setStatus("idle");
      toast.error(f.error);
    }
  };

  const inputClass =
    "w-full border border-[#E5E5E5] bg-white px-4 py-3 text-[#0A0A0A] outline-none transition-colors placeholder:text-[#B0B0B0] focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10";
  const labelClass = "mb-2 block text-sm font-medium text-[#0A0A0A]";

  return (
    <div data-testid="contact-page">
      <PageHeader overline={t.contact.overline} title={t.contact.title} subtitle={t.contact.subtitle} />

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-5 lg:gap-16 lg:px-10 lg:py-28">
          {/* Info */}
          <Reveal className="lg:col-span-2">
            <h2 className="text-2xl font-semibold tracking-tight text-[#0A0A0A]">
              {t.contact.infoTitle}
            </h2>
            <ul className="mt-8 space-y-7">
              <li className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#0A0A0A]">
                  <MapPin size={20} className="text-white" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#757575]">
                    {t.contact.addressLabel}
                  </div>
                  <div className="mt-1 text-[#0A0A0A]">
                    {contact.street}
                    <br />
                    {contact.city}
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#0A0A0A]">
                  <Phone size={20} className="text-white" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#757575]">
                    {t.contact.phoneLabel}
                  </div>
                  <a href={telHref(contact.phone)} data-testid="contact-phone-link" className="mt-1 block text-[#0A0A0A] hover:text-[#E8620E]">
                    {contact.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#0A0A0A]">
                  <Mail size={20} className="text-white" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#757575]">
                    {t.contact.emailLabel}
                  </div>
                  <a href={mailHref(contact.email)} data-testid="contact-email-link" className="mt-1 block text-[#0A0A0A] hover:text-[#E8620E]">
                    {contact.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#0A0A0A]">
                  <Globe size={20} className="text-white" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#757575]">Web</div>
                  <div className="mt-1 text-[#0A0A0A]">{contact.website}</div>
                </div>
              </li>
            </ul>
            <div className="mt-8">
              <LocationMap lat={Number(contact.lat)} lng={Number(contact.lng)} />
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="lg:col-span-3">
            {status === "success" ? (
              <div
                className="flex h-full flex-col items-start justify-center border border-[#E5E5E5] bg-[#FAFAFA] p-10"
                data-testid="contact-success"
              >
                <CheckCircle2 size={48} className="text-[#E8620E]" />
                <h3 className="mt-6 text-2xl font-semibold text-[#0A0A0A]">{f.success}</h3>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 border border-[#0A0A0A] px-6 py-3 text-sm font-semibold text-[#0A0A0A] transition-colors hover:bg-[#0A0A0A] hover:text-white"
                  data-testid="contact-reset-button"
                >
                  {f.newRequest}
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="border border-[#E5E5E5] p-8 lg:p-10" data-testid="contact-form">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>{f.name}</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      required
                      placeholder={f.namePlaceholder}
                      className={inputClass}
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{f.email}</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      placeholder={f.emailPlaceholder}
                      className={inputClass}
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{f.phone}</label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder={f.phonePlaceholder}
                      className={inputClass}
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{f.subject}</label>
                    <input
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      required
                      placeholder={f.subjectPlaceholder}
                      className={inputClass}
                      data-testid="contact-input-subject"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className={labelClass}>{f.message}</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    rows={6}
                    placeholder={f.messagePlaceholder}
                    className={`${inputClass} resize-none`}
                    data-testid="contact-input-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="group mt-8 flex w-full items-center justify-center gap-2 bg-[#E8620E] px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#C4520C] disabled:opacity-60 sm:w-auto"
                  data-testid="contact-submit-button"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> {f.sending}
                    </>
                  ) : (
                    <>
                      {f.submit}
                      <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
